import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import MobileNavBar from '../common/MobileNavBar';
import MobileFooter from '../MobileFooter';
import MobileBottomNav from '../common/MobileBottomNav';
import { convertToVwMob } from '../../Utils/responsive';
import { useAppSelector } from '../../store/hooks';
import { selectUserProfile } from '../../Module/User/Presentation/userSelectors';
import MissingDocumentsModal from '../common/MissingDocumentsModal';
import { MISSING_DOCS_MODAL_SEEN_KEY } from '../../Utils/missingSettlementDocsModal';
import { isIndividualUserType } from '../../Utils/userType';
import { useLocation } from 'react-router-dom';
import { PATHS } from '../../routes/paths';

const SUPPRESS_POST_SIGNUP_MODALS_KEY = 'missionfund.suppressPostSignupModals';
const SIGNUP_SKIP_ACCOUNT_FIRST_KEY = 'missionfund.signupSkippedAccountFirst';


type Props = {
  title?: string;
  children: ReactNode;
  footerRef?: React.Ref<HTMLElement>;
  hideHeader?: boolean;
  hideFooter?: boolean;
  showBottomNav?: boolean;
  /** 스크롤 영역(본문+푸터) 하단 패딩 — 페이지 내 `position:fixed` 하단 바가 있을 때 푸터가 가려지지 않게 */
  mainPaddingBottom?: string;
};

const MobilePageLayout: React.FC<Props> = ({
  title,
  children,
  footerRef,
  hideHeader,
  hideFooter,
  showBottomNav = true,
  mainPaddingBottom,
}) => {
  const location = useLocation();
  const userProfile = useAppSelector(selectUserProfile);
  const [isMissingDocsModalOpen, setIsMissingDocsModalOpen] = useState(false);

  useEffect(() => {
    // 로그인, 회원가입 페이지에서는 안내 모달 띄우지 않음
    if (
      location.pathname === PATHS.LOGIN ||
      location.pathname === PATHS.LOGIN_EMAIL ||
      location.pathname === PATHS.SIGNUP ||
      location.pathname === PATHS.PASSWORD_FIND ||
      location.pathname === PATHS.DONATION_HISTORY_FIND
    ) {
      setIsMissingDocsModalOpen(false);
      return;
    }

    // 나의 계정 화면에서는 정산 서류 등록 안내 팝업을 띄우지 않음 (저장 직후 바로 뜨는 것 방지)
    // (삭제됨: 이제 저장 시 플래그를 세팅하므로 페이지 체크 불필요)

    // 회원가입 환영 화면에서 '나중에 하기'로 온 경우: 메인 프로젝트(/project)에서는 안내 모달을 띄우지 않음
    if (typeof window !== 'undefined' && localStorage.getItem(SIGNUP_SKIP_ACCOUNT_FIRST_KEY) === '1') {
      if (location.pathname === PATHS.PROJECT_MANAGEMENT || location.pathname === PATHS.DASHBOARD) {
        return;
      }
      localStorage.removeItem(SIGNUP_SKIP_ACCOUNT_FIRST_KEY);
    }

    // 회원가입 직후 나의 계정으로 이동한 경우: 모달 비표시. 그 외 경로로 이동 시 플래그 해제
    if (typeof window !== 'undefined' && localStorage.getItem(SUPPRESS_POST_SIGNUP_MODALS_KEY) === '1') {
      if (location.pathname === PATHS.MY_ACCOUNT || location.pathname.startsWith(`${PATHS.MY_ACCOUNT}/`)) {
        return;
      }
      localStorage.removeItem(SUPPRESS_POST_SIGNUP_MODALS_KEY);
    }

    if (userProfile) {
      const isKorea = userProfile.bank_location_country_code === 'KR' || !userProfile.bank_location_country_code;
      const hasBankInfo = Boolean(userProfile.bank_account || userProfile.bank_name);
      const missingBankbook = !userProfile.bankbook_image_url;
      const missingId = !userProfile.registration_card_url;
      const isIndividual = isIndividualUserType(userProfile.user_type);
      const isOrganizationWithoutUniqueId = !isIndividual && !userProfile.business_registration;
      const needsId = isIndividual || !isOrganizationWithoutUniqueId;
      const shouldShowMissingDocs = isKorea && hasBankInfo && (missingBankbook || (needsId && missingId));

      if (shouldShowMissingDocs) {
        let shouldOpenDocs = true;
        if (typeof window !== 'undefined') {
          const hasShownDocs = localStorage.getItem(MISSING_DOCS_MODAL_SEEN_KEY) === '1';
          shouldOpenDocs = !hasShownDocs;
        }
        if (shouldOpenDocs) {
          if (typeof window !== 'undefined') {
            localStorage.setItem(MISSING_DOCS_MODAL_SEEN_KEY, '1');
          }
          setIsMissingDocsModalOpen(true);
        } else if (isMissingDocsModalOpen) {
          // 이미 열려있다면 유지
        } else {
          setIsMissingDocsModalOpen(false);
        }
      } else {
        setIsMissingDocsModalOpen(false);
      }
    } else {
      // 로그아웃 상태 등 userProfile이 없으면 모달 닫기
      setIsMissingDocsModalOpen(false);
    }
  }, [userProfile, location.pathname, isMissingDocsModalOpen]);

  const handleCloseMissingDocsModal = () => {
    setIsMissingDocsModalOpen(false);
  };

  return (
    <PageBackground>
      <Shell>
        {!hideHeader && <MobileNavBar title={title} />}
        <Main showBottomNav={showBottomNav} $paddingBottom={mainPaddingBottom} data-mobile-scroll-main>
          <Content $showBottomNav={showBottomNav}>{children}</Content>
          {!hideFooter && <MobileFooter ref={footerRef} showBottomNav={showBottomNav} />}
        </Main>
        {showBottomNav && <MobileBottomNav />}
      </Shell>
      <MissingDocumentsModal
        isOpen={isMissingDocsModalOpen}
        onClose={handleCloseMissingDocsModal}
        userProfile={userProfile}
      />
    </PageBackground>
  );
};

const PageBackground = styled.div`
  height: 100dvh;
  background-color: #F9FAFB;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Shell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Main = styled.main<{ showBottomNav: boolean; $paddingBottom?: string }>`
  flex: 1;
  padding: 0;
  padding-bottom: ${({ $paddingBottom }) => $paddingBottom ?? '0'};
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

const Content = styled.div<{ $showBottomNav: boolean }>`
  box-sizing: border-box;
  width: 100%;
  min-height: 40vh;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  color: ${({ theme }) => theme.colors.grayScale[80]};
  padding-top: ${convertToVwMob(16)};
  padding-right: ${convertToVwMob(16)};
  padding-left: ${convertToVwMob(16)};
  padding-bottom: ${({ $showBottomNav }) =>
    $showBottomNav
      ? `calc(${convertToVwMob(24)} + ${convertToVwMob(100)} + env(safe-area-inset-bottom))`
      : convertToVwMob(24)};
`;

export default MobilePageLayout;


